// RecipeBuilderModal — visual recipe over the source's `events` view.
//
// User vision (2026-05-09): "u toolu vidjeti podatke i moci ih premapirati,
// posloziti i to napraviti jednostavno. A da sva ta preslagivanja mogu
// vrijediti za materijalizirane tablice kasnije koje ce ici kroz stream.
// sve hookove koje sam imao zelim kroz ovu funkcionalnost."
//
// One UX, two materialisations:
//   Phase 1 (today): recipe → BQ view over GCS via SELECT … FROM events.
//                    Operator picks fields, sets filter, picks dedupe key,
//                    saves. Live preview shows rows after each tweak.
//   Phase 2 (P5):    same recipe → native BQ table maintained by the
//                    auto-tier classifier. Same view name, same schema —
//                    operator never refactors.
//   Phase 3:         "On match" toggle creates a forward_webhook trigger
//                    bound to the same predicate (replaces the standalone
//                    forward_webhook trigger surface).
//
// SQL is generated from the recipe state — the textarea is read-only and
// shown for transparency. Operator can drop to raw SQL via "Edit SQL" at
// the bottom of the modal (deferred — for now, recipe-only).

import React, { useEffect, useMemo, useState } from 'react';
import type { TriggerAction } from '@datahub/shared';
import {
  createBqView,
  createTrigger,
  createView,
  getEventsSchemaFlat,
  previewBqView,
  type SchemaField,
} from '../../api';
import { useToast } from '../Toast';
import { buildRecipeSql, filterRowsToTree, type FieldPick, type FilterRow } from './recipe-sql';

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontFamily: 'var(--mono)',
  fontSize: 12,
  border: '1px solid var(--line)',
  borderRadius: 4,
  background: 'var(--bg)',
  color: 'var(--ink)',
};

export function RecipeBuilderModal({ sourceId, dataset, baseView, onCancel, onSaved }: {
  sourceId: string;
  dataset: string;
  baseView: string;
  onCancel: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schema, setSchema] = useState<SchemaField[] | null>(null);
  const [picks, setPicks] = useState<FieldPick[]>([]);
  const [filters, setFilters] = useState<FilterRow[]>([{ field_path: '', op: 'is_present', value: '' }]);
  const [uniqueByPath, setUniqueByPath] = useState('');
  const [uniqueOrderBy, setUniqueOrderBy] = useState<'received_at_desc' | 'received_at_asc'>('received_at_desc');
  const [submitting, setSubmitting] = useState(false);
  const [previewRows, setPreviewRows] = useState<Array<Record<string, unknown>> | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Stream-time action — what (if anything) the recipe also DOES at
  // ingest time. None = view-only (default). The other options create a
  // trigger alongside the BQ view so the same recipe drives both
  // analytics and per-event processing.
  type StreamAction = 'none' | 'validate' | 'dedupe' | 'forward' | 'enrich';
  const [streamAction, setStreamAction] = useState<StreamAction>('none');
  const [forwardUrl, setForwardUrl] = useState('');
  const [forwardMethod, setForwardMethod] = useState<'POST' | 'PUT'>('POST');
  const [enrichUrl, setEnrichUrl] = useState('');
  const [enrichIdPath, setEnrichIdPath] = useState('');
  const [enrichMergePath, setEnrichMergePath] = useState('enrichment');
  const [dedupeWindowSec, setDedupeWindowSec] = useState(3600);

  // Load payload schema once. Surfaces the dot-paths inside payload so the
  // operator picks fields by name, not raw JSON path.
  useEffect(() => {
    // Flat shape: recipe builder constructs BQ projections; recursive
    // dotted paths would alias to invalid BQ identifiers.
    getEventsSchemaFlat(sourceId)
      .then(r => setSchema(r.fields))
      .catch(() => setSchema([]));
  }, [sourceId]);

  // Auto-collect "interesting" fields the first time schema arrives so
  // the operator sees a non-trivial recipe from second one.
  useEffect(() => {
    if (!schema || picks.length > 0) return;
    if (schema.length === 0) return;
    const meaningful = schema.filter(f => f.path !== 'payload' && f.path !== 'origin');
    const auto: FieldPick[] = meaningful.slice(0, 5).map(f => {
      const cast: FieldPick['cast'] =
        f.types.includes('number') ? 'float' :
        f.types.includes('boolean') ? 'bool' : 'string';
      return { source_path: f.path, alias: f.path.replace(/[^a-zA-Z0-9_]/g, '_'), cast };
    });
    if (auto.length > 0) setPicks(auto);
  }, [schema, picks.length]);

  const sql = useMemo(
    () => buildRecipeSql({ dataset, baseView, picks, filters, uniqueByPath, uniqueOrderBy }),
    [dataset, baseView, picks, filters, uniqueByPath, uniqueOrderBy],
  );

  // Debounced live preview — re-runs on SQL change.
  useEffect(() => {
    setPreviewLoading(true);
    setPreviewError(null);
    const handle = setTimeout(() => {
      previewBqView(sourceId, sql, 10)
        .then(r => { setPreviewRows(r.rows); setPreviewLoading(false); })
        .catch(e => { setPreviewError(String(e)); setPreviewLoading(false); });
    }, 600);
    return () => { clearTimeout(handle); setPreviewLoading(false); };
  }, [sourceId, sql]);

  function addPick(path: string): void {
    if (picks.some(p => p.source_path === path)) return;
    const f = schema?.find(s => s.path === path);
    const cast: FieldPick['cast'] =
      f?.types.includes('number') ? 'float' :
      f?.types.includes('boolean') ? 'bool' : 'string';
    setPicks(prev => [...prev, { source_path: path, alias: path.replace(/[^a-zA-Z0-9_]/g, '_'), cast }]);
  }
  function removePick(path: string): void {
    setPicks(prev => prev.filter(p => p.source_path !== path));
  }
  function updatePick(idx: number, patch: Partial<FieldPick>): void {
    setPicks(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }
  function updateFilter(idx: number, patch: Partial<FilterRow>): void {
    setFilters(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  }
  function addFilter(): void { setFilters(prev => [...prev, { field_path: '', op: 'is_present', value: '' }]); }
  function removeFilter(idx: number): void { setFilters(prev => prev.filter((_, i) => i !== idx)); }

  /** Build the trigger TriggerAction body from the current recipe + stream
   *  action selector. Returns null when no trigger should be created. */
  function buildTriggerAction(): { action: TriggerAction; needsViewScope: boolean } | null {
    if (streamAction === 'none') return null;
    if (streamAction === 'validate') {
      // Only `is_present` filter rows map cleanly — validate_required
      // takes a fields[] presence list. Other ops (eq, gt, …) are
      // expressed in the view's WHERE but can't gate at ingest time
      // through validate_required's contract today.
      const fields = filters
        .filter(f => f.field_path.trim() && f.op === 'is_present')
        .map(f => f.field_path.trim());
      if (fields.length === 0) {
        toast.show('Validate gate needs at least one "is present" filter row.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'validate_required', fields }, needsViewScope: false };
    }
    if (streamAction === 'dedupe') {
      if (!uniqueByPath.trim()) {
        toast.show('Dedupe gate needs the "unique on key" path set.', { kind: 'warning' });
        return null;
      }
      return {
        action: { type: 'validate_dedupe', key_path: uniqueByPath.trim(), window_sec: Number(dedupeWindowSec) || 3600 },
        needsViewScope: false,
      };
    }
    if (streamAction === 'forward') {
      if (!forwardUrl.trim()) {
        toast.show('Forward needs a target URL.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'forward_webhook', url: forwardUrl.trim(), method: forwardMethod }, needsViewScope: true };
    }
    if (streamAction === 'enrich') {
      if (!enrichUrl.trim() || !enrichIdPath.trim() || !enrichMergePath.trim()) {
        toast.show('Enrich needs URL, id_path, and merge_path.', { kind: 'warning' });
        return null;
      }
      return {
        action: { type: 'enrich_fetch', url: enrichUrl.trim(), id_path: enrichIdPath.trim(), merge_path: enrichMergePath.trim(), method: 'GET' },
        needsViewScope: true,
      };
    }
    return null;
  }

  async function handleSave(): Promise<void> {
    if (!name.trim()) {
      toast.show('Pick a name for the new view.', { kind: 'warning' });
      return;
    }
    // Validate stream-action config BEFORE creating the BQ view so the
    // operator sees the warning without a half-saved state.
    const triggerSpec = streamAction === 'none' ? null : buildTriggerAction();
    if (streamAction !== 'none' && !triggerSpec) return;
    setSubmitting(true);
    try {
      // Always: create the BQ view. This is the analytics surface; the
      // trigger (if any) is the per-event processing surface — same
      // recipe, two materialisations.
      await createBqView(sourceId, { name: name.trim(), sql, description: description.trim() || undefined });
      // Optional trigger creation
      if (triggerSpec) {
        // Build a FilterTree-based view to scope the trigger when needed
        // (forward + enrich). The FilterTree mirrors the recipe's filter
        // rows so the SAME predicate runs both at query time (BQ view
        // WHERE) and at ingest time (filter-eval matchesEvent before
        // trigger fires).
        let viewId: string | undefined;
        if (triggerSpec.needsViewScope) {
          const tree = filterRowsToTree(filters);
          if (tree) {
            const ftView = await createView(sourceId, {
              name: `${name.trim()}__filter`,
              filter: tree,
              columns: [],
            });
            viewId = ftView.view_id;
          }
        }
        await createTrigger(sourceId, {
          name: `${name.trim()}__${streamAction}`,
          action: triggerSpec.action,
          ...(viewId ? { view_id: viewId } : {}),
        });
        toast.show(`Created view "${name.trim()}" + ${streamAction} trigger.`, { kind: 'success' });
      } else {
        toast.show(`Created view "${name.trim()}".`, { kind: 'success' });
      }
      onSaved();
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const availableFields = (schema ?? []).filter(f => f.path !== 'payload' && f.path !== 'origin');

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'oklch(0.18 0.01 250 / 0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 32 }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, width: 'min(960px, 95vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>Build a view from {baseView}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
            Pick fields, set filters, optionally dedupe — saves as <code style={{ fontFamily: 'var(--mono)' }}>{dataset}.&lt;name&gt;</code>. SQL preview below shows what will be queried.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'minmax(0, 280px) minmax(0, 1fr)', gap: 0 }}>
          {/* LEFT: schema field picker */}
          <div style={{ padding: '14px 16px', borderRight: '1px solid var(--line)', background: 'var(--bg-sunken)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Available fields</div>
            {schema === null && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Loading schema…</div>}
            {schema !== null && availableFields.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>No payload fields yet — send an event to populate.</div>}
            {availableFields.map(f => {
              const picked = picks.some(p => p.source_path === f.path);
              return (
                <button
                  key={f.path}
                  type="button"
                  onClick={() => picked ? removePick(f.path) : addPick(f.path)}
                  style={{
                    textAlign: 'left', padding: '6px 8px', cursor: 'pointer',
                    background: picked ? 'var(--accent-soft)' : 'var(--bg)',
                    border: `1px solid ${picked ? 'var(--accent-line)' : 'var(--line)'}`,
                    borderRadius: 4, color: 'var(--ink)',
                  }}
                  title={`occurrences: ${f.occurrences} · types: ${f.types.join(', ')}\n\nSamples: ${f.sample_values.slice(0, 3).map(v => JSON.stringify(v)).join(', ')}`}
                >
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink)' }}>{f.path}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-3)' }}>{f.types.join(', ')} · {f.occurrences}×</div>
                </button>
              );
            })}
          </div>

          {/* RIGHT: name + picks + filters + unique + SQL + preview */}
          <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="recent_orders"
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </div>

            {picks.length > 0 && (
              <div>
                <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Picked fields ({picks.length})</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                  {picks.map((p, idx) => (
                    <div key={p.source_path} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <code style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', minWidth: 110 }}>{p.source_path}</code>
                      <span style={{ color: 'var(--ink-3)' }}>→</span>
                      <input
                        value={p.alias}
                        onChange={e => updatePick(idx, { alias: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <select
                        value={p.cast}
                        onChange={e => updatePick(idx, { cast: e.target.value as FieldPick['cast'] })}
                        style={{ ...inputStyle, padding: '6px 6px' }}
                      >
                        <option value="string">string</option>
                        <option value="float">float</option>
                        <option value="int">int</option>
                        <option value="bool">bool</option>
                        <option value="json">json</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => removePick(p.source_path)}
                        style={{ background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, padding: '5px 8px', cursor: 'pointer', color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 12 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Filters (AND)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                {filters.map((f, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      value={f.field_path}
                      onChange={e => updateFilter(idx, { field_path: e.target.value })}
                      placeholder="payload field path"
                      style={{ ...inputStyle, flex: 1 }}
                      list={`schema-${idx}`}
                    />
                    <datalist id={`schema-${idx}`}>
                      {availableFields.map(s => <option key={s.path} value={s.path} />)}
                    </datalist>
                    <select
                      value={f.op}
                      onChange={e => updateFilter(idx, { op: e.target.value as FilterRow['op'] })}
                      style={inputStyle}
                    >
                      <option value="is_present">is present</option>
                      <option value="is_empty">is empty</option>
                      <option value="eq">=</option>
                      <option value="neq">!=</option>
                      <option value="gt">&gt;</option>
                      <option value="lt">&lt;</option>
                      <option value="contains">contains</option>
                    </select>
                    {!['is_present', 'is_empty'].includes(f.op) && (
                      <input
                        value={f.value}
                        onChange={e => updateFilter(idx, { value: e.target.value })}
                        placeholder="value"
                        style={{ ...inputStyle, flex: 1 }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeFilter(idx)}
                      style={{ background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, padding: '5px 8px', cursor: 'pointer', color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 12 }}
                    >×</button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFilter}
                  style={{ alignSelf: 'flex-start', background: 'transparent', border: '1px dashed var(--line)', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', color: 'var(--ink-2)', fontSize: 11.5 }}
                >+ filter</button>
              </div>
            </div>

            <div>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
                Unique on key <span style={{ textTransform: 'none', color: 'var(--ink-3)' }}>· optional dedupe</span>
              </label>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <input
                  value={uniqueByPath}
                  onChange={e => setUniqueByPath(e.target.value)}
                  placeholder="payload field path (e.g. order_id) — leave empty to keep all rows"
                  style={{ ...inputStyle, flex: 1 }}
                  list="unique-schema"
                />
                <datalist id="unique-schema">
                  {availableFields.map(s => <option key={s.path} value={s.path} />)}
                </datalist>
                <select
                  value={uniqueOrderBy}
                  onChange={e => setUniqueOrderBy(e.target.value as 'received_at_desc' | 'received_at_asc')}
                  style={inputStyle}
                  disabled={!uniqueByPath.trim()}
                >
                  <option value="received_at_desc">last wins</option>
                  <option value="received_at_asc">first wins</option>
                </select>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
                Also apply at ingest <span style={{ textTransform: 'none', color: 'var(--ink-3)' }}>· optional trigger</span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {([
                  { id: 'none', label: 'View only', blurb: 'Recipe is a BQ view only — query-time logic.' },
                  { id: 'validate', label: 'Validate gate', blurb: 'Drop events failing the recipe\'s "is present" filters at ingest.' },
                  { id: 'dedupe', label: 'Dedupe gate', blurb: 'Drop duplicates at ingest using the unique-on-key path.' },
                  { id: 'forward', label: 'Forward (POST)', blurb: 'POST matching events to an external URL.' },
                  { id: 'enrich', label: 'Enrich', blurb: 'Fetch external URL using a payload field, merge response back.' },
                ] as const).map(opt => {
                  const active = streamAction === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setStreamAction(opt.id)}
                      style={{
                        padding: '4px 10px', fontSize: 11.5, fontFamily: 'var(--ui)',
                        background: active ? 'var(--accent-soft)' : 'var(--bg)',
                        border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line)'}`,
                        color: active ? 'var(--accent)' : 'var(--ink-2)',
                        borderRadius: 14, cursor: 'pointer',
                        fontWeight: active ? 500 : 400,
                      }}
                      title={opt.blurb}
                    >{opt.label}</button>
                  );
                })}
              </div>
              {streamAction !== 'none' && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {streamAction === 'validate' && (
                    <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                      Saving creates a <code style={{ fontFamily: 'var(--mono)' }}>validate_required</code> trigger with the recipe's <code style={{ fontFamily: 'var(--mono)' }}>is present</code> filter rows as required fields. Events failing the gate are quarantined; downstream forward/promote triggers skip them.
                      <div style={{ marginTop: 4, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                        Required fields: {filters.filter(f => f.op === 'is_present' && f.field_path.trim()).map(f => f.field_path.trim()).join(', ') || '(add at least one "is present" row above)'}
                      </div>
                    </div>
                  )}
                  {streamAction === 'dedupe' && (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                        Saving creates a <code style={{ fontFamily: 'var(--mono)' }}>validate_dedupe</code> trigger keyed on <code style={{ fontFamily: 'var(--mono)' }}>{uniqueByPath || '(set unique-on-key above)'}</code>. The second event with the same key inside the window quarantines.
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', minWidth: 86 }}>Window (sec)</span>
                        <input
                          type="number" min={1} max={604800}
                          value={dedupeWindowSec}
                          onChange={e => setDedupeWindowSec(Number(e.target.value) || 3600)}
                          style={inputStyle}
                        />
                        <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>(default 3600 = 1h, max 604800 = 7d)</span>
                      </div>
                    </>
                  )}
                  {streamAction === 'forward' && (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                        Saving creates a <code style={{ fontFamily: 'var(--mono)' }}>forward_webhook</code> trigger scoped to the recipe's filter (only matching events forward). HMAC-signed; SSRF-blocked.
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          value={forwardUrl}
                          onChange={e => setForwardUrl(e.target.value)}
                          placeholder="https://example.com/webhook"
                          style={{ ...inputStyle, flex: 1 }}
                        />
                        <select
                          value={forwardMethod}
                          onChange={e => setForwardMethod(e.target.value as 'POST' | 'PUT')}
                          style={inputStyle}
                        >
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                        </select>
                      </div>
                    </>
                  )}
                  {streamAction === 'enrich' && (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                        Saving creates an <code style={{ fontFamily: 'var(--mono)' }}>enrich_fetch</code> trigger. Substitutes <code style={{ fontFamily: 'var(--mono)' }}>{'{id}'}</code> in the URL with <code style={{ fontFamily: 'var(--mono)' }}>payload[id_path]</code>, merges the JSON response into the event at <code style={{ fontFamily: 'var(--mono)' }}>merge_path</code>, emits a derivative event.
                      </div>
                      <input
                        value={enrichUrl}
                        onChange={e => setEnrichUrl(e.target.value)}
                        placeholder="https://api.example.com/users/{id}"
                        style={inputStyle}
                      />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          value={enrichIdPath}
                          onChange={e => setEnrichIdPath(e.target.value)}
                          placeholder="id_path · e.g. customer_id"
                          style={{ ...inputStyle, flex: 1 }}
                        />
                        <input
                          value={enrichMergePath}
                          onChange={e => setEnrichMergePath(e.target.value)}
                          placeholder="merge_path · e.g. customer"
                          style={{ ...inputStyle, flex: 1 }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Description <span style={{ textTransform: 'none', color: 'var(--ink-3)' }}>· optional</span></label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="What this view exposes — for downstream consumers"
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginTop: 4 }}
              />
            </div>

            <div>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Generated SQL</label>
              <pre style={{
                margin: '4px 0 0', padding: '10px 12px',
                background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4,
                fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-2)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto',
              }}>{sql}</pre>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Live preview</label>
                {previewLoading && <span style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>refreshing…</span>}
                {previewError && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{previewError.slice(0, 100)}</span>}
                {!previewLoading && previewRows && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>· {previewRows.length} row{previewRows.length === 1 ? '' : 's'}</span>}
              </div>
              {previewRows && previewRows.length > 0 && (
                <div style={{ marginTop: 6, overflow: 'auto', maxHeight: 220, border: '1px solid var(--line)', borderRadius: 4 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--mono)', fontSize: 11 }}>
                    <thead style={{ background: 'var(--bg-sunken)', position: 'sticky', top: 0 }}>
                      <tr>
                        {Object.keys(previewRows[0] ?? {}).map(c => <th key={c} style={{ padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid var(--line)', fontWeight: 500, color: 'var(--ink-2)' }}>{c}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
                          {Object.keys(previewRows[0] ?? {}).map(c => (
                            <td key={c} style={{ padding: '4px 8px', color: 'var(--ink)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {formatCell(row[c])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {previewRows && previewRows.length === 0 && !previewError && (
                <div style={{ marginTop: 4, padding: 16, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 4, fontSize: 12, color: 'var(--ink-3)' }}>No rows match yet. Adjust filters or wait for events.</div>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn sm ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button
            className="btn sm primary"
            onClick={() => { void handleSave(); }}
            disabled={submitting || !name.trim() || !!previewError}
          >
            {submitting
              ? 'Saving…'
              : streamAction === 'none' ? 'Save view'
              : `Save view + ${streamAction} trigger`}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 120 ? v.slice(0, 117) + '…' : v;
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 120);
  return String(v);
}
